const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

// P1-1: the access token lives only in memory; the refresh token lives in an
// httpOnly cookie set by the API. Neither is written to localStorage, so XSS
// cannot exfiltrate a durable credential. The user profile (display data,
// not a credential) stays in localStorage to avoid a UI flash on reload.
const USER_KEY = "ai_insurance_user";
const LEGACY_ACCESS_KEY = "ai_insurance_access_token";
const LEGACY_REFRESH_KEY = "ai_insurance_refresh_token";

let accessToken: string | null = null;

export function getStoredAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getStoredUser() {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function persistSession(newAccessToken: string, user: unknown) {
  accessToken = newAccessToken;
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  accessToken = null;
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(LEGACY_ACCESS_KEY);
  localStorage.removeItem(LEGACY_REFRESH_KEY);
}

/** One-time migration: consume a pre-cookie refresh token left in localStorage. */
function takeLegacyRefreshToken(): string | null {
  const legacy = localStorage.getItem(LEGACY_REFRESH_KEY);
  localStorage.removeItem(LEGACY_ACCESS_KEY);
  localStorage.removeItem(LEGACY_REFRESH_KEY);
  return legacy;
}

export class ApiError extends Error {
  status: number;
  detail: string;
  upgradeUrl?: string;
  payload?: Record<string, unknown>;

  constructor(status: number, detail: string, upgradeUrl?: string, payload?: Record<string, unknown>) {
    super(detail);
    this.status = status;
    this.detail = detail;
    this.upgradeUrl = upgradeUrl;
    this.payload = payload;
  }
}

type RequestOptions = RequestInit & {
  skipAuth?: boolean;
  _retried?: boolean;
};

let refreshPromise: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  // The refresh token arrives via httpOnly cookie; a legacy localStorage
  // token (pre-cookie sessions) is sent once in the body, then discarded.
  const legacyToken = takeLegacyRefreshToken();
  const response = await fetch(`${API_BASE}/v1/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(legacyToken ? { refresh_token: legacyToken } : {}),
  });

  if (!response.ok) {
    clearSession();
    return null;
  }

  const data = await response.json();
  persistSession(data.access_token, data.user);
  return data.access_token as string;
}

async function getValidAccessToken(): Promise<string | null> {
  return getStoredAccessToken();
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuth, _retried, ...fetchOptions } = options;
  const headers = new Headers(fetchOptions.headers);
  if (!headers.has("Content-Type") && fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  if (!skipAuth) {
    const token = await getValidAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  let response = await fetch(`${API_BASE}${path}`, { credentials: "include", ...fetchOptions, headers });

  if (response.status === 401 && !skipAuth && !_retried) {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }
    const newToken = await refreshPromise;
    if (newToken) {
      headers.set("Authorization", `Bearer ${newToken}`);
      response = await fetch(`${API_BASE}${path}`, {
        credentials: "include",
        ...fetchOptions,
        headers,
        _retried: true,
      } as RequestInit);
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    const detail =
      typeof payload === "object" && payload && "detail" in payload
        ? String((payload as { detail: unknown }).detail)
        : `Request failed (${response.status})`;
    const upgradeUrl =
      typeof payload === "object" && payload && "upgrade_url" in payload
        ? String((payload as { upgrade_url: unknown }).upgrade_url)
        : undefined;
    if (response.status === 402 && upgradeUrl && typeof window !== "undefined") {
      window.location.assign(upgradeUrl);
    }
    const errorPayload =
      typeof payload === "object" && payload ? (payload as Record<string, unknown>) : undefined;
    throw new ApiError(response.status, detail, upgradeUrl, errorPayload);
  }

  return payload as T;
}

export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const token = getStoredAccessToken();
  const response = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(response.status, `Download failed (${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const name = match ? match[1] : fallbackName;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string) => apiRequest<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => apiRequest<T>(path, { method: "DELETE" }),
};
